import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { CrossIcon, TrashIcon, UserIcon } from "../../assets";
import { Button, Input, Selector } from "../../components";
import { StaffRole } from "../../constants";
import { getLinkForUploadImage } from "../../services/auth.service";
import { uploadFile } from "../../services/file.service";
import { updateStaff } from "../../services/staff.service";
import styles from "./styles.module.scss";
import { toast } from "react-hot-toast";
import { IStaff } from "../../models";
import { createStaffValidationScheme } from "../AddNewStaff/validation";
import { useFormik } from "formik";
import { usePasswordValidation } from "../../hooks";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onGetAllStaff: () => void;
  item: IStaff;
}

const EditStaff: FC<Props> = ({
  isOpen,
  setIsOpen,
  onGetAllStaff,
  item,
}): JSX.Element => {
  const [email, setEmail] = useState<string>(item?.email);
  const [fullName, setFullName] = useState<string>(item?.fullName);
  const [role, setRole] = useState<string>(item?.type || "Moderator");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [avatar, setAvatar] = useState<string>(item?.image || "");
  const [avatarFile, setAvatarFile] = useState<
    ArrayBuffer | null | undefined | string | any
  >();
  const [avatarFileName, setAvatarFileName] = useState<string>("");

  useEffect(() => {
    setEmail(item?.email);
    setFullName(item?.fullName);
    setAvatar(item?.image || "");
  }, [item, isOpen]);

  const onDeleteAvatar = () => {
    setAvatar("");
    setAvatarFile(undefined);
    setAvatarFileName("");
  };

  useEffect(() => {
    if (
      email?.length > 3 &&
      fullName.length > 1 &&
      fullName.length <= 50 &&
      (email !== item.email ||
        fullName !== item.fullName ||
        avatar !== item.image)
    ) {
    } else {
    }
  }, [email, fullName, avatar, item]);

  const onCreateStaff = async (
    email: string,
    password: string,
    fullName: string,
    setFieldError: (
      field: "email" | "password" | "fullName",
      message: string
    ) => void
  ) => {
    try {
      setIsLoading(true);
      let imageLink = "";
      if (avatarFile) {
        //@ts-ignore
        const resLinkImg = await getLinkForUploadImage(avatarFileName);

        imageLink = resLinkImg?.data?.data?.downloadUrl;
        let blob = new Blob([avatarFile], { type: avatarFile.type });

        await uploadFile(blob, resLinkImg?.data?.data?.uploadUrl);
      } else if (avatar) {
        imageLink = avatar;
      }

      const body: any = {
        adminId: item.id,
      };

      if (email !== item.email) {
        body.email = email;
      }
      if (password) {
        body.password = password;
      }
      if (fullName !== item.fullName) {
        body.fullName = fullName;
      }
      body.type = role;
      if (avatar) {
        body.image = imageLink;
      } else {
        body.image = null;
      }
      const { data } = await updateStaff(body);
      if (data?.success) {
        onGetAllStaff();
        setIsOpen(false);
        setEmail("");
        setFullName("");
        setAvatar("");
        setAvatarFile(undefined);
        setAvatarFileName("");
        toast.success("User was successfully updated");
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);

      const issues = Array.isArray(error?.message) ? error.message : null;

      if (issues) {
        const emailIssue = issues.find((i: any) => i?.path?.[0] === "email");
        const passwordIssue = issues.find(
          (i: any) => i?.path?.[0] === "password"
        );
        const fullNameIssue = issues.find(
          (i: any) => i?.path?.[0] === "fullName"
        );
        setFieldError("email", emailIssue?.message || "");
        setFieldError("password", passwordIssue?.message || "");
        setFieldError("fullName", fullNameIssue?.message || "");

        if (!emailIssue && !passwordIssue && !fullNameIssue) {
          toast.error(issues[0]?.message || "Failed to update user");
        }
      } else {
        toast.error(
          typeof error?.message === "string"
            ? error.message
            : "Failed to update user"
        );
      }
    }
  };

  const inputFile = useRef<HTMLInputElement>(null);

  const showOpenFileDialog = () => {
    inputFile.current?.click();
  };

  const onSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      setAvatar(URL.createObjectURL(e.target.files[0]));
      let file = e.target.files[0];
      setAvatarFile(e.target.files[0]);

      setAvatarFileName(file?.name);
    } catch (error) {
      console.log(error);
    }
  };

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    errors,
    resetForm,
  } = useFormik({
    validationSchema: createStaffValidationScheme,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      fullName: item?.fullName,
      email: item?.email,
      password: "",
    },
    onSubmit: (values, { setFieldError }) => {
      onCreateStaff(
        values.email,
        values.password,
        values.fullName,
        setFieldError
      );
    },
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);
  const passwordError = usePasswordValidation(values.password);

  return (
    <Modal
      isOpen={isOpen}
      shouldFocusAfterRender={false}
      onRequestClose={() => setIsOpen(false)}
      overlayClassName={styles.overlay}
      className={styles.modal}
      ariaHideApp={false}
    >
      <div className={styles.innerContainer}>
        <h5 className={styles.title}>Edit Manager</h5>
        <CrossIcon
          onClick={() => setIsOpen(false)}
          className={styles.closeBtn}
        />
        <div className={styles.header}>
          <div onClick={showOpenFileDialog} className={styles.avatarBtn}>
            {avatar && (
              <img src={avatar} alt={"avatar"} className={styles.avatar} />
            )}
            {!avatar && <UserIcon />}
            {avatar ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteAvatar();
                }}
                className={styles.deleteAvatarButton}
              >
                <TrashIcon />
              </div>
            ) : null}
          </div>

          <input
            style={{ display: "none" }}
            ref={inputFile}
            type={"file"}
            accept="image/*"
            onChange={onSelectFile}
            onClick={(e: any) => (e.target.value = null)}
            className={styles.bootcampsSettingsUploadImgBtn}
          />
          <p className={styles.avatarTitle}>Change Avatar</p>
        </div>

        <>
          <div className={styles.inputsContainer}>
            <Input
              label="Full Name"
              value={values.fullName}
              onChange={handleChange("fullName")}
              onBlur={handleBlur("fullName")}
              placeholder="Full Name"
              autoComplete="new-password"
              error={touched.fullName ? errors.fullName : ""}
            />
            <Input
              label="Email"
              value={values.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              placeholder="example@gmail.com"
              autoComplete="new-password"
              error={touched.email ? errors.email : ""}
            />
            <Input
              label="Password"
              value={values.password}
              onChange={handleChange("password")}
              onBlur={handleBlur("password")}
              placeholder="password"
              type={"password"}
              autoComplete="new-password"
              error={values.password ? passwordError : ""}
            />
            <Selector
              label="Role"
              value={role}
              setValue={setRole}
              data={StaffRole}
            />
          </div>
          <Button
            title="Save"
            styleType={"filled"}
            loading={isLoading}
            onClick={() => handleSubmit()}
            size="small"
          />
        </>
      </div>
    </Modal>
  );
};

export default EditStaff;
